import React, { useEffect, useState, useMemo, useRef } from "react";
import { TextInput } from "@woocommerce/blocks-components";
import Select from "react-select";
// import './style.scss';
import { __ } from '@wordpress/i18n';

import { isValidPhone, isValidPassword, getCheckboxLabel, isValidURL, isValidEmail, formatFieldPriceDisplay, formatOptionPriceDisplay, getFieldPriceType, getUserMetaValueForField, getCustomValidatorFormatHint, getBuiltinValidatorFormatHint, shouldApplyTypeFormatRule } from '../helper/input-validations';
import { getDatepickerOptions, resolveDefaultDateFromField, applyDefaultDateToInput, formatStoredDateForDatepicker } from '../helper/datepicker-config';
import { getTimepickerInitConfig, resolveLinkedDateValue, resolveLinkedDateFieldName, generateTimeSlots } from '../helper/timepicker-config';
import { applyInputMask, getInputMaskConfig, getInputMaskFormatHint, hasInputMask } from '../helper/inputmask-config';
import { renderHeadingStylingField } from '../helper/heading-styling-config';

const FormatHintNote = ({ hint }) => {
    if (!hint) {
        return null;
    }

    return (
        <span
            className="ccfw-input-mask-hint description"
            style={{ display: 'block', marginTop: '15px', marginBottom: '5px', fontSize: '0.85em', color: '#646970' }}
        >
            {hint}
        </span>
    );
};
const MaskedInputField = ({ field, field_name, field_label, fieldValue, priceDisplay, localValue, setLocalValue, isError, required, onChange, onBlur, field_price, inputType, fieldRef, showTelNote, isValidPhoneFn, t }) => {
    const formatHint = getInputMaskFormatHint(field);
    const maskConfig = getInputMaskConfig(field);
    const maskControllerRef = useRef(null);
    const inputRef = useRef(null);
    const pendingCursorRef = useRef(null);

    useEffect(() => {
        if (!maskConfig) {
            maskControllerRef.current = null;
            return undefined;
        }

        let cancelled = false;
        let attempt = 0;

        const tryInit = () => {
            if (cancelled) {
                return;
            }

            if (typeof window.fmeCcfwCreateInputMaskController !== 'function') {
                if (attempt < 5) {
                    attempt += 1;
                    setTimeout(tryInit, 200);
                } else {
                    maskControllerRef.current = null;
                }
                return;
            }

            const controller = window.fmeCcfwCreateInputMaskController(maskConfig);
            const result = controller.init(fieldValue || localValue || '');
            maskControllerRef.current = controller;

            if (result.masked !== localValue) {
                setLocalValue(result.masked);
            }
        };

        tryInit();

        return () => {
            cancelled = true;
        };
        }, [field_name, maskConfig?.mask, maskConfig?.maskType, fieldValue]);

    useEffect(() => {
        const el = inputRef.current || document.getElementById(field_name);
        if (!el || pendingCursorRef.current === null) {
            return;
        }

        const pos = pendingCursorRef.current;
        pendingCursorRef.current = null;

        requestAnimationFrame(() => {
            try {
                el.setSelectionRange(pos, pos);
            } catch (e) {
                // Ignore inputs that do not support selection.
            }
        });
    }, [localValue, field_name]);

    const assignInputRef = (node) => {
        inputRef.current = node;

        if (typeof fieldRef === 'function') {
            fieldRef(node);
        } else if (fieldRef) {
            fieldRef.current = node;
        }
    };

    const handleChange = (value) => {
        const controller = maskControllerRef.current;

        if (!controller) {
            setLocalValue(applyInputMask(value, field));
            return;
        }

        const result = controller.applyInput(localValue, value);
        setLocalValue(result.masked);
    };

    const handlePaste = (e) => {
        const controller = maskControllerRef.current;
        if (!controller) {
            return;
        }

        const clipboard = e.clipboardData || window.clipboardData;
        if (!clipboard || typeof clipboard.getData !== 'function') {
            return;
        }

        const pasted = clipboard.getData('text');
        if (pasted === null || pasted === undefined) {
            return;
        }

        e.preventDefault();
        const result = controller.applyPaste(pasted);
        pendingCursorRef.current = result.masked.length;
        setLocalValue(result.masked);
    };

    const handleKeyDown = (e) => {
        const controller = maskControllerRef.current;
        const el = inputRef.current || document.getElementById(field_name);

        if (!controller || !el || typeof el.selectionStart !== 'number') {
            return;
        }

        if (e.key !== 'Backspace' && e.key !== 'Delete') {
            return;
        }

        const result = controller.handleKeydown(e.key, el.selectionStart, el.selectionEnd);

        if (result && result.handled) {
            e.preventDefault();

            if (result.masked !== undefined) {
                if (result.cursorPos !== undefined) {
                    pendingCursorRef.current = result.cursorPos;
                }
                setLocalValue(result.masked);
            }
        }
    };

    const handleBlur = () => {
        // Sync value then validate via parent.
        if (inputType === 'tel') {
            onChange(field_name, localValue, field, field_label);
        } else {
            onChange(field_name, localValue, field, field_label, field_price);
        }
        onBlur(field, localValue);
    };

    const wrapperClass = inputType === 'tel' ? 'wc-block-components-tel-input' : 'wc-block-components-text-input';

    return (
        <div className={wrapperClass}>
            <TextInput
                label={inputType === 'tel' ? field_label : field_label + priceDisplay}
                id={field_name}
                type={inputType}
                className={isError ? 'has-error' : ''}
                value={localValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={handleBlur}
                required={required}
                ref={assignInputRef}
            />
            {formatHint && (
                <FormatHintNote hint={formatHint} />
            )}
            {showTelNote && localValue && isValidPhoneFn && !isValidPhoneFn(localValue) && (
                <>
                    <br />
                    <span className="wc-block-components-validation-error" style={{ color: '#d63638' }}>
                        {t('phone_validation_error', __('Please enter a valid phone number (8-15 digits, + optional)', 'conditional-checkout-fields-for-woocommerce'))}
                    </span>
                </>
            )}
        </div>
    );
};

const DatePickerField = ({ field, field_name, field_label, priceDisplay, localValue, setLocalValue, isError, required, onChange, onBlur, field_price }) => {
    const inputRef = useRef(null);
    const datepickerReadyRef = useRef(false);

    const getInput = () => (window.jQuery && inputRef.current ? window.jQuery(inputRef.current) : null);

    const normalizeDateValue = (value) => {
        if (!value) {
            return '';
        }
        return formatStoredDateForDatepicker(field, value) || value;
    };

    const setupDatepicker = ($input, valueToApply) => {
        if (!$input || !$input.length || typeof $input.datepicker !== 'function') {
            return false;
        }

        const formattedValue = normalizeDateValue(valueToApply);
        const options = getDatepickerOptions(field);
        options.onSelect = (dateText) => {
            setLocalValue(dateText);
            onChange(field_name, dateText, field, field_label, field_price);
            $input.val(dateText);
        };

        if ($input.hasClass('hasDatepicker')) {
            try {
                $input.datepicker('destroy');
            } catch (e) {
                $input.removeClass('hasDatepicker');
            }
        }

        if (formattedValue) {
            options.defaultDate = formattedValue;
        }

        $input.datepicker(options);
        datepickerReadyRef.current = true;

        if (formattedValue) {
            $input.val(formattedValue);
            try {
                $input.datepicker('setDate', formattedValue);
            } catch (e) {
                // Keep typed value when datepicker cannot parse the stored format.
            }
        } else {
            applyDefaultDateToInput($input, options);
        }

        return true;
    };

    const applyDateValue = (valueToApply, attempt = 0) => {
        const formattedValue = normalizeDateValue(valueToApply);
        const $input = getInput();
        if (!$input || !$input.length) {
            if (attempt < 8) {
                setTimeout(() => applyDateValue(valueToApply, attempt + 1), 200);
            }
            return;
        }

        if (!datepickerReadyRef.current || !$input.hasClass('hasDatepicker')) {
            if (!setupDatepicker($input, formattedValue) && attempt < 8) {
                setTimeout(() => applyDateValue(valueToApply, attempt + 1), 200);
            }
            return;
        }

        if (!formattedValue) {
            $input.val('');
            try {
                $input.datepicker('setDate', null);
            } catch (e) {
                // ignore
            }
            return;
        }

        if ($input.val() !== formattedValue) {
            $input.val(formattedValue);
        }
        try {
            $input.datepicker('setDate', formattedValue);
        } catch (e) {
            // Keep typed value when datepicker cannot parse the stored format.
        }
    };

    const defaultAppliedRef = useRef(false);

    useEffect(() => {
        defaultAppliedRef.current = false;
    }, [field_name, field.date_picker_settings, field.is_min_date]);

    useEffect(() => {
        if (defaultAppliedRef.current || localValue) {
            return;
        }

        const userMetaDate = normalizeDateValue(getUserMetaValueForField(field));
        if (userMetaDate) {
            defaultAppliedRef.current = true;
            setLocalValue(userMetaDate);
            onChange(field_name, userMetaDate, field, field_label, field_price);
            return;
        }

        const resolvedDefault = resolveDefaultDateFromField(field);
        if (!resolvedDefault) {
            return;
        }

        defaultAppliedRef.current = true;
        setLocalValue(resolvedDefault);
        onChange(field_name, resolvedDefault, field, field_label, field_price);
    }, [field_name, field.date_picker_settings, field.is_min_date, localValue, field, field_label, field_price, onChange]);

    useEffect(() => {
        datepickerReadyRef.current = false;
        const $input = getInput();
        if ($input && $input.length) {
            setupDatepicker($input, localValue);
        }
    }, [field_name, field.date_picker_settings, field.is_min_date, localValue]);

    useEffect(() => {
        applyDateValue(localValue);
    }, [localValue, field_name, field.date_picker_settings, field.is_min_date]);

    return (
        <div className="wc-block-components-date-input show_label_within_border ccfw-date-picker-block">
            <label htmlFor={field_name}>
                {field_label}
                {priceDisplay}
            </label>
            <input
                ref={inputRef}
                id={field_name}
                type="text"
                autoComplete="off"
                readOnly
                className={`fme-ccfw-date-picker ${isError ? 'has-error' : ''}`}
                style={{ cursor: 'pointer', padding: '0.7em', width: '100%', border: isError ? '1px solid red' : '1.5px solid #777', borderRadius: '6px', backgroundColor: 'transparent' }}
                value={localValue || ''}
                onBlur={() => {
                    onChange(field_name, localValue, field, field_label, field_price);
                    onBlur(field, localValue);
                }}
                required={required}
            />
        </div>
    );
};

const TimePickerField = ({ field, field_name, field_label, priceDisplay, localValue, setLocalValue, isError, required, onChange, onBlur, field_price, allFieldValues }) => {
    const selectRef = useRef(null);
    const lastSyncedValueRef = useRef(localValue);
    const baseFieldName = field.field_name || field_name;
    const config = useMemo(() => getTimepickerInitConfig(field), [field.time_picker_settings]);
    const linkedDateValue = useMemo(
        () => resolveLinkedDateValue(config.linkedDate, field_name, baseFieldName, allFieldValues),
        [config.linkedDate, field_name, baseFieldName, allFieldValues]
    );
    const slots = useMemo(
        () => generateTimeSlots(config, linkedDateValue),
        [config, linkedDateValue]
    );
    const isEffectivelyRequired = required && slots.length > 0;

    const getTimepickerEventNs = ($select) => {
        return '.fmeCcfwTimepicker_' + ($select.attr('id') || $select.attr('name') || 'tp');
    };

    useEffect(() => {
        const $ = window.jQuery;
        const $select = $ ? $(selectRef.current) : null;
        if (!$select || !$select.length) {
            return undefined;
        }

        $select.attr('data-fme-timepicker-config', JSON.stringify(config));

        if (typeof window.fmeCcfwInitTimepicker === 'function') {
            window.fmeCcfwInitTimepicker($select, config);
        }

        const handleChange = () => {
            const val = $select.val() || '';
            if (lastSyncedValueRef.current === val) {
                return;
            }
            lastSyncedValueRef.current = val;
            setLocalValue(val);
            onChange(field_name, val, field, field_label, field_price);
        };

        $select.off('change.fmeCcfwTpBlock').on('change.fmeCcfwTpBlock', handleChange);

        const linkedDate = config.linkedDate || '';

        return () => {
            $select.off('change.fmeCcfwTpBlock');
            if (linkedDate) {
                const cleanupNs = getTimepickerEventNs($select);
                const resolvedLinked = resolveLinkedDateFieldName(linkedDate, field_name, baseFieldName, allFieldValues);
                let linkedSelector = '[name="' + resolvedLinked + '"], #' + resolvedLinked;
                if (resolvedLinked !== linkedDate) {
                    linkedSelector += ', [name="' + linkedDate + '"], #' + linkedDate;
                } else {
                    linkedSelector += ', [name^="' + linkedDate + '_"]';
                }
                $(document).off('change' + cleanupNs + ' input' + cleanupNs, linkedSelector);
            }
        };
    }, [field_name, field_label, field_price, config, linkedDateValue, field.time_picker_settings]);

    useEffect(() => {
        const $ = window.jQuery;
        const $select = $ ? $(selectRef.current) : null;
        if (!$select || !$select.length) {
            return;
        }

        const escapedValue = (localValue || '').replace(/"/g, '\\"');
        const currentVal = $select.val() || '';

        if (localValue && localValue !== '0' && $select.find(`option[value="${escapedValue}"]`).length) {
            if (currentVal !== localValue) {
                $select.val(localValue);
                lastSyncedValueRef.current = localValue;
            }
            return;
        }

        if ((!localValue || localValue === '0') && currentVal) {
            $select.val('');
            lastSyncedValueRef.current = '';
            if (localValue !== '') {
                setLocalValue('');
            }
            return;
        }

        if (localValue && localValue !== '0' && !$select.find(`option[value="${escapedValue}"]`).length) {
            $select.val('');
            lastSyncedValueRef.current = '';
            setLocalValue('');
            onChange(field_name, '', field, field_label, field_price);
        }
    }, [localValue, linkedDateValue]);

    return (
        <div className="wc-block-components-select-input show_label_within_border ccfw-time-picker-block">
            <label htmlFor={field_name}>
                {field_label}
                {priceDisplay}
            </label>
            <select
                ref={selectRef}
                key={field_name}
                id={field_name}
                name={field_name}
                className={`fme-ccfw-time-picker fme-ccfw_class_main ${isError ? 'has-error' : ''}`}
                data-fme-field-type="time_picker"
                data-fme-timepicker-config={JSON.stringify(config)}
                style={{ padding: '0.7em', width: '100%', border: isError ? '1px solid red' : '1.5px solid #777', borderRadius: '6px', backgroundColor: 'transparent' }}
                onBlur={() => onBlur(field, localValue)}
                required={isEffectivelyRequired}
            />
        </div>
    );
};

const DynamicField = ({ field, fieldValue, error, onChange, onBlur, cart, repeatPrice, allFieldValues }) => {

    const i18n = fme_upload_data?.i18n || {};
    const t = (key, fallback) => i18n[key] || fallback;

    const currencySymbol = fme_upload_data?.currency_symbol || '';
    const { field_name, field_type, field_label, is_required, options, fieldRef } = field;

    let { field_price } = field;
    const [filePreview, setFilePreview] = useState(null);
    const [localValue, setLocalValue] = useState(fieldValue);
    const required = is_required === 1;
    const isError = !!error;
    const fileInputRef = useRef(null);
    field_price = repeatPrice ? field_price : 0;

    // Sync local value when fieldValue changes from parent
    useEffect(() => {
        if (field_type === 'date_picker' && fieldValue) {
            setLocalValue(formatStoredDateForDatepicker(field, fieldValue) || fieldValue);
            return;
        }
        setLocalValue(fieldValue);
    }, [fieldValue, field_type, field.date_picker_settings, field.is_min_date]);

    // Display price if applicable  - with field label
    let fieldPrice = field.field_price;
    let priceDisplay = formatFieldPriceDisplay(fieldPrice, getFieldPriceType(field), currencySymbol, field);
    priceDisplay = field_price == 0 ? '' : priceDisplay;
    const validatorFormatHint = getCustomValidatorFormatHint(field) || getBuiltinValidatorFormatHint(field);

    // Handle file removal
    const handleRemoveImage = async () => {

        // Optional: if file has a name or ID from upload
        const fileName = filePreview.name;

        // Remove preview and clear file input
        setFilePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = null; // reset input field

            // Create a synthetic event to trigger the onChange handler
            const event = new Event('change', { bubbles: true });
            fileInputRef.current.dispatchEvent(event);
        }
        
        if (fileName) {
            try {
                jQuery.ajax({
                    url: fme_upload_data.ajax_url,
                    type: "POST",
                    data: {
                        action: "fme_ccfw_delete_file",
                        fileName: fileName,
                        security: fme_upload_data.upload_nonce,
                    },
                    success: (response) => {
                    },
                    error: (xhr, status, error) => {
                        console.error("AJAX error:", error);
                    },
                });
            } catch (error) {
                console.error("Try-catch error:", error);
            }
        }

    };

    // Multiselect / Select: options
    const reactOptions = useMemo(() => {
        return Array.isArray(options)
            ?
            options.map(([label, key, actualValue, isDefault]) => ({
                label: !repeatPrice ? label : label + formatOptionPriceDisplay(actualValue, field, currencySymbol),
                key,
                actualValue,
                isDefault,
                value: `${key}::${actualValue}` // unique
            }))
            : [];
    }, [options, repeatPrice, currencySymbol, field]);


    //Select / Multiselect: prepare selected values
    const selectedValues = useMemo(() => {
        if (Array.isArray(fieldValue)) {
            return fieldValue.map(item =>
                reactOptions.find(opt => opt.key === item.key)
            ).filter(Boolean);
        } else {
            return reactOptions.filter(opt => fieldValue == opt.key) || null;
        }

    }, [fieldValue, reactOptions]);

    // Handle select / multiselect change
    const handleSelectChange = (selected) => {
        if (!selected) {
            onChange(field_name, null);
            return;
        }

        // Multiselect (array of selected items)
        if (Array.isArray(selected)) {
            const cleaned = selected.map(({ key, actualValue }) => ({
                key,
                value: !repeatPrice ? 0 : actualValue
            }));
            const addPrice = cleaned.reduce((sum, item) => sum + Number(item.value || 0), 0);
            onChange(field_name, cleaned, field, field_label, addPrice);
        } else {
            // Single select
            const addPrice = !repeatPrice ? 0 : selected.actualValue;
            onChange(field_name, selected.key, field, selected.label, addPrice);
        }
    };

    switch (field_type) {

        case 'textarea':
            return (
                <div className="wc-block-components-textarea-input ccfw-textarea-block">
                    <label htmlFor={field_name}>
                        {field_label}
                        {priceDisplay}
                        {field.is_required == 1 && <abbr className="required" title="required">*</abbr>}
                    </label>
                    <textarea
                        id={field_name}
                        className={`wc-block-components-textarea${isError ? ' has-error' : ''}`}
                        value={localValue || ''}
                        onChange={(e) => setLocalValue(e.target.value)}
                        onBlur={() => {
                            onChange(field_name, localValue, field, field_label, field_price);
                            onBlur(field, localValue);
                        }}
                        required={required}
                        rows={4}
                    />
                    <FormatHintNote hint={validatorFormatHint} />
                </div>
            );

        case 'checkbox':
            return (
                <>
                    <br />
                    <div className="wc-block-components-checkbox">
                        <label htmlFor={field_name}>
                            <input
                                type="checkbox"
                                id={field_name}
                                checked={!!fieldValue}
                                onChange={(e) => onChange(field_name, e.target.checked ? field.field_placeholder ?? 'yes' : '', field, field_label, field_price)}
                                onBlur={() => onBlur(field)}
                                required={required}
                                className="wc-block-components-checkbox__input"
                            />
                            <svg
                                className="wc-block-components-checkbox__mark"
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 20"
                            >
                                <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
                            </svg>
                            <span
                                className="wc-block-components-checkbox__label"
                                dangerouslySetInnerHTML={{ __html: getCheckboxLabel(field_label) }}
                            />

                            {priceDisplay}
                            {field.is_required == 1 && <abbr className="required" title="required">*</abbr>}
                        </label>
                    </div>
                </>
            );

        case 'file':
            return (
                <div className="wc-block-components-file_upload ccfw-file-upload-section">
                    <label htmlFor={field_name} className="wc-block-components-file-upload__label">
                        {field_label}
                        {priceDisplay}
                        {field.is_required == 1 && <abbr className="required" title="required">*</abbr>}
                    </label>
                    <br />
                    <input
                        type="file"
                        id={field_name}
                        name={field_name}
                        className={`wc-block-components-file-upload__input ${isError ? 'has-error' : ''}`}
                        accept="image/application/pdf"
                        onChange={(e) => {
                            const file = e.target.files[0];

                            if (file) {
                                onChange(field_name, file, field, field_label, field_price); // ✅ Store metadata only
                                setFilePreview(file); // Store actual File for preview
                            } else {
                                onChange(field_name, null, field, field_label, 0);
                                setFilePreview(null);
                            }
                        }}
                        onBlur={() => onBlur(field)}
                        required={required}
                        ref={fileInputRef}
                    />
                    {filePreview && filePreview.type?.startsWith("image/") && (
                        <div className="wc-block-components-file-upload__preview" style={{ position: 'relative', maxWidth: '200px' }}>
                            <a
                                onClick={() => {
                                    handleRemoveImage();
                                    onChange(field_name, null, field, field_label, 0);
                                }}
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    right: 0,
                                    background: "red",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "2px",
                                    width: "24px",
                                    height: "24px",
                                    cursor: "pointer",
                                    zIndex: 10,
                                    textDecoration: "none",
                                    textAlign: "center",
                                    lineHeight: "24px",
                                }}
                            >
                                &times;
                            </a>
                            <img
                                src={URL.createObjectURL(filePreview)}
                                alt="Preview"
                                className="wc-block-components-file-upload__image-preview"
                                style={{ maxWidth: "100%", marginTop: "10px", display: "block" }}
                            />
                        </div>
                    )}
                </div>
            );

        case 'radio':
            const groupName = `radio-control-wc-${field_name}-options`;
            // Prepare selected value (for controlled component)
            const selectedValue = fieldValue ?? '';

            return (
                <>
                    <br />
                    <div className="wc-block-components-radio-control__root ccfw-radio-block">
                        {/* Field label as legend */}
                        {field?.field_label && (
                            <strong className="wc-block-components-radio-control__legend">
                                {field.field_label}
                                {field.is_required == 1 && <abbr className="required" title="required">*</abbr>}
                            </strong>
                        )}

                        <div
                            className="wc-block-components-radio-control wc-block-components-radio-control--highlight-checked"
                            role="radiogroup"
                            aria-labelledby={`radio-group-label-${field_name}`}
                        >
                            {options.map((option) => {
                                const [label, value, , isDefault = false, content = null] = option;
                                let [, , price = ''] = option;

                                price = !repeatPrice ? '' : price;

                                const optionId = `${groupName}-${value}`;
                                let isChecked = selectedValue === value;

                                if ('' != fieldValue && value == isDefault) {
                                    // isChecked = true;
                                }

                                let optLabel = field?.field_label ? field.field_label + ' - ' + label : label;

                                return (
                                    <div
                                        key={value}
                                        className={`wc-block-components-radio-control__option ${isChecked ? 'wc-block-components-radio-control__option--checked' : ''
                                            }`}
                                            style={ isChecked ? { border: '1px solid #121212cc', borderRadius: '4px' } : {} }
                                    >
                                        <input
                                            id={optionId}
                                            className="wc-block-components-radio-control__input"
                                            type="radio"
                                            name={groupName}
                                            value={value}
                                            checked={isChecked}
                                            onChange={() => onChange(field_name, value, field, optLabel, price)}
                                            aria-describedby={content ? `${optionId}-description` : undefined}
                                        />

                                        <label
                                            htmlFor={optionId}
                                            className="wc-block-components-radio-control__label"
                                        >
                                            <div className="wc-block-components-radio-control__option-layout">
                                                {/* Main label */}
                                                <span className="wc-block-components-radio-control__label-content">
                                                    <span className="wc-block-components-radio-control__label-text">
                                                        {label}
                                                    </span>
                                                </span>

                                                {/* Optional extra content / description */}
                                                {content || optLabel && isChecked && (
                                                    <div
                                                        id={`${optionId}-description`}
                                                        className="wc-block-components-radio-control__description "
                                                    >
                                                        {content || optLabel}

                                                        {price && price !== '' && (
                                                            ' ( ' + currencySymbol + ' ' + price + ')'
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    </div>

                                );
                            })}
                        </div>
                    </div>
                </>
            );

        case 'number':
            return (
                <div className="wc-block-components-text-input">
                    <TextInput
                        label={field_label + priceDisplay}
                        id={field_name}
                        type="number"
                        className={isError ? 'has-error' : ''}
                        value={localValue}
                        onChange={(value) => setLocalValue(value)}
                        onBlur={() => {
                            onChange(field_name, localValue, field, field_label, field_price);
                            onBlur(field, localValue);
                        }}
                        required={required}
                    />
                    <FormatHintNote hint={validatorFormatHint} />
                </div>
            );

        case 'tel': {
            // Custom/confirm/other built-ins replace the tel format rule; builtin:tel or type default still apply.
            const applyTelRules = shouldApplyTypeFormatRule(field, 'tel');
            if (hasInputMask(field)) {
                return (
                    <MaskedInputField
                        field={field}
                        field_name={field_name}
                        field_label={field_label}
                        fieldValue={fieldValue}
                        priceDisplay={priceDisplay}
                        localValue={localValue}
                        setLocalValue={setLocalValue}
                        isError={isError}
                        required={required}
                        onChange={onChange}
                        onBlur={onBlur}
                        field_price={field_price}
                        inputType="tel"
                        showTelNote={applyTelRules}
                        isValidPhoneFn={isValidPhone}
                        t={t}
                    />
                );
            }
            return (
                <div className="wc-block-components-tel-input">
                    <TextInput
                        label={field_label}
                        id={field_name}
                        type="tel"
                        className={isError ? 'has-error' : ''}
                        value={localValue}
                        onChange={(value) => setLocalValue(value)}
                        onBlur={() => {
                            onChange(field_name, localValue, field, field_label);
                            onBlur(field, localValue);
                        }}
                        required={required}
                        {...(applyTelRules ? { pattern: "^(\\+[0-9\\- +]+)?[0-9]+$" } : {})}
                    />
                    {validatorFormatHint ? (
                        <FormatHintNote hint={validatorFormatHint} />
                    ) : (
                        applyTelRules && (
                            <span className="ccfw-tel-input-note">{t('tel_example', __('e.g. +1234567890', 'conditional-checkout-fields-for-woocommerce'))}</span>
                        )
                    )}
                    {applyTelRules && localValue && !isValidPhone(localValue) && (
                        <>
                            <br />
                            <span className="wc-block-components-validation-error" style={{ color: '#d63638' }}>
                                {t('phone_validation_error', __('Please enter a valid phone number (8-15 digits, + optional)', 'conditional-checkout-fields-for-woocommerce'))}
                            </span>
                        </>
                    )}
                </div>
            );
        }

        case 'password':
            const [showPassword, setShowPassword] = useState(false);
            // Custom/confirm/other built-ins replace the password strength rule.
            const applyPasswordRules = shouldApplyTypeFormatRule(field, 'password');

            return (
                <div className="wc-block-components-password-input">
                    <div className="input-with-icon" style={{ position: "relative" }}>
                        <TextInput
                            label={field_label}
                            id={field_name}
                            type={showPassword ? "text" : "password"}
                            className={isError ? 'has-error' : ''}
                            value={localValue}
                            onChange={(value) => setLocalValue(value)}
                            onBlur={() => {
                                onChange(field_name, localValue, field, field_label);
                                onBlur(field, localValue);
                            }}
                            required={required}
                            {...(applyPasswordRules ? { pattern: "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$" } : {})}
                            autocomplete="off"
                        />
                        <span
                            className="toggle-password-visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            title={showPassword ? t('hide_password', __('Hide password', 'conditional-checkout-fields-for-woocommerce')) : t('show_password', __('Show password', 'conditional-checkout-fields-for-woocommerce'))}
                            aria-label={showPassword ? t('hide_password', __('Hide password', 'conditional-checkout-fields-for-woocommerce')) : t('show_password', __('Show password', 'conditional-checkout-fields-for-woocommerce'))}
                            style={{ position: "absolute", right: "15px", top: "13px", cursor: "pointer", zIndex: 2, textAlign: "left" }}
                        >
                            {showPassword ? "🔓" : "🔒"}
                        </span>
                    </div>
                    {applyPasswordRules && (
                        <span className="ccfw-password-input-note">{t('password_note', __('Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char', 'conditional-checkout-fields-for-woocommerce'))}</span>
                    )}
                    <FormatHintNote hint={validatorFormatHint} />
                    {applyPasswordRules && localValue && !isValidPassword(localValue) && (
                        <>
                            <br />
                            <span className="wc-block-components-validation-error" style={{ color: '#d63638' }}>
                                {t('password_validation_error', __('Password must contain at least 8 characters, 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character (@$!%*?&)', 'conditional-checkout-fields-for-woocommerce'))}
                            </span>
                        </>
                    )}
                </div>
            );

        case 'date':
            return (
                <div className="wc-block-components-date-input show_label_within_border">
                    <label htmlFor={field_name}>
                        {field_label}
                        {priceDisplay}
                    </label>
                    <input
                        id={field_name}
                        type="date"
                        className={isError ? 'has-error' : ''}
                        style={{ cursor: 'pointer', padding: '0.7em', width: '100%', borderColor: isError ? 'red' : 'initial' }}
                        onClick={(e) => e.target.showPicker && e.target.showPicker()}
                        value={localValue}
                        onChange={(e) => {
                            const nextValue = e.target.value;
                            setLocalValue(nextValue);
                            onChange(field_name, nextValue, field, field_label, field_price);
                        }}
                        onBlur={() => {
                            onChange(field_name, localValue, field, field_label, field_price);
                            onBlur(field, localValue);
                        }}
                        min={field.is_min_date == false && new Date().toISOString().split('T')[0]}
                        required={required}
                    />
                </div>
            );

        case 'date_picker':
            return (
                <DatePickerField
                    field={field}
                    field_name={field_name}
                    field_label={field_label}
                    priceDisplay={priceDisplay}
                    localValue={localValue}
                    setLocalValue={setLocalValue}
                    isError={isError}
                    required={required}
                    onChange={onChange}
                    onBlur={onBlur}
                    field_price={field_price}
                />
            );

        case 'email': {
            // Custom/confirm/other built-ins replace the email format rule.
            const applyEmailRules = shouldApplyTypeFormatRule(field, 'email');
            return (
                <div className="wc-block-components-email-input">
                    <TextInput
                        label={field_label + priceDisplay}
                        id={field_name}
                        type="email"
                        className={isError ? 'has-error' : ''}
                        value={localValue}
                        onChange={(value) => setLocalValue(value)}
                        onBlur={() => {
                            onChange(field_name, localValue, field, field_label, field_price);
                            onBlur(field, localValue);
                        }}
                        required={required}
                    />
                    <FormatHintNote hint={validatorFormatHint} />
                    {applyEmailRules && localValue && !isValidEmail(localValue) && (
                        <>
                            <br />
                            <span className="wc-block-components-validation-error" style={{ color: '#d63638' }}>
                                {t('email_validation_error', __('Please enter a valid email address', 'conditional-checkout-fields-for-woocommerce'))}
                            </span>
                        </>
                    )}
                </div>
            );
        }

        case 'hidden':
            return (
                <input
                    type="hidden"
                    id={field_name}
                    name={field_name}
                    value={localValue || field.field_placeholder || ''}
                    onChange={(e) => onChange(field_name, e.target.value, field, field_label, field_price)}
                />
            );

        case 'checkbox_group': {
            const normalizeGroupKey = (key) => String(key ?? '').replace(/\s/g, '');
            const selectedGroupValues = Array.isArray(fieldValue)
                ? fieldValue.map((item) => normalizeGroupKey(typeof item === 'object' ? item.key : item))
                : (fieldValue
                    ? (typeof fieldValue === 'string' && fieldValue.startsWith('[')
                        ? (() => {
                            try {
                                const parsed = JSON.parse(fieldValue);
                                return Array.isArray(parsed)
                                    ? parsed.map((item) => normalizeGroupKey(typeof item === 'object' ? item.key : item))
                                    : [];
                            } catch (e) {
                                return String(fieldValue).split(',').filter(Boolean).map(normalizeGroupKey);
                            }
                        })()
                        : String(fieldValue).split(',').filter(Boolean).map(normalizeGroupKey))
                    : []);

            return (
                <>
                    <br />
                    <div className="wc-block-components-checkbox-group ccfw-checkbox-group-block">
                        <strong className="wc-block-components-checkbox-group__legend">
                            {field.field_label}
                            {field.is_required == 1 && <abbr className="required" title="required">*</abbr>}
                        </strong>
                        <div className="fme-ccfw-checkbox-group">
                            {(options || []).map((option) => {
                                const [label, value, price = ''] = option;
                                const optionKey = normalizeGroupKey(value);
                                const optionId = `${field_name}_${optionKey}`;
                                const isChecked = selectedGroupValues.includes(optionKey);

                                return (
                                    <div key={optionKey} className={`wc-block-components-checkbox${isChecked ? ' wc-block-components-checkbox--checked' : ''}`}>
                                        <label htmlFor={optionId}>
                                            <input
                                                type="checkbox"
                                                id={optionId}
                                                checked={isChecked}
                                                onChange={(e) => {
                                                    let nextValues = [...selectedGroupValues];
                                                    if (e.target.checked) {
                                                        if (!nextValues.includes(optionKey)) {
                                                            nextValues.push(optionKey);
                                                        }
                                                    } else {
                                                        nextValues = nextValues.filter((item) => item !== optionKey);
                                                    }

                                                    const cleaned = nextValues.map((key) => {
                                                        const matched = (options || []).find((opt) => normalizeGroupKey(opt[1]) === key);
                                                        return {
                                                            key: matched ? matched[1] : key,
                                                            value: !repeatPrice ? 0 : (matched?.[2] || 0),
                                                        };
                                                    });

                                                    onChange(field_name, cleaned, field, field_label, cleaned.reduce((sum, item) => sum + Number(item.value || 0), 0));
                                                }}
                                                onBlur={() => onBlur(field)}
                                                className="wc-block-components-checkbox__input"
                                            />
                                            <svg
                                                className="wc-block-components-checkbox__mark"
                                                aria-hidden="true"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 20"
                                            >
                                                <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
                                            </svg>
                                            <span className="wc-block-components-checkbox__label">{label}</span>
                                            {repeatPrice && price ? formatOptionPriceDisplay(price, field, currencySymbol) : ''}
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            );
        }
            
        case 'datetime-local':
            return (
                <div className="wc-block-components-datetime-local-input show_label_within_border">
                    <label htmlFor={field_name}>
                        {field_label}
                        {priceDisplay}
                    </label>
                    <input
                        id={field_name}
                        type="datetime-local"
                        className={isError ? 'has-error' : ''}
                        style={{ cursor: 'pointer', padding: '0.7em', width: '100%', borderColor: isError ? 'red' : 'initial' }}
                        onClick={(e) => e.target.showPicker && e.target.showPicker()}
                        value={localValue}
                        onChange={(e) => {
                            const nextValue = e.target.value;
                            setLocalValue(nextValue);
                            onChange(field_name, nextValue, field, field_label, field_price);
                        }}
                        onBlur={() => {
                            onChange(field_name, localValue, field, field_label, field_price);
                            onBlur(field, localValue);
                        }}
                        min={field.is_min_date == false && new Date().toISOString().split('T')[0]}
                        required={required}
                    />
                </div>
            );
            
        case 'time':
            return (
                <div className="wc-block-components-time-input show_label_within_border">
                    <label>
                        {field_label}
                        {priceDisplay}
                    </label>
                    <input
                        id={field_name}
                        type="time"
                        className={isError ? 'has-error' : ''}
                        style={{ cursor: 'pointer', padding: '0.7em', width: '100%', borderColor: isError ? 'red' : 'initial' }}
                        onClick={(e) => e.target.showPicker && e.target.showPicker()}
                        value={localValue}
                        onChange={(e) => setLocalValue(e.target.value)}
                        onBlur={() => {
                            onChange(field_name, localValue, field, field_label, field_price);
                            onBlur(field, localValue);
                        }}
                        required={required}
                    />
                </div>
            );

        case 'time_picker':
            return (
                <TimePickerField
                    field={field}
                    field_name={field_name}
                    field_label={field_label}
                    priceDisplay={priceDisplay}
                    localValue={localValue}
                    setLocalValue={setLocalValue}
                    isError={isError}
                    required={required}
                    onChange={onChange}
                    onBlur={onBlur}
                    field_price={field_price}
                    allFieldValues={allFieldValues}
                />
            );

        case 'week':
            return (
                <div className="wc-block-components-week-input show_label_within_border">
                    <label>
                        {field_label}
                        {priceDisplay}
                    </label>
                    <input
                        id={field_name}
                        type="week"
                        className={isError ? 'has-error' : ''}
                        style={{ cursor: 'pointer', padding: '0.7em', width: '100%', borderColor: isError ? 'red' : 'initial' }}
                        onClick={(e) => e.target.showPicker && e.target.showPicker()}
                        value={localValue}
                        onChange={(e) => setLocalValue(e.target.value)}
                        onBlur={() => {
                            onChange(field_name, localValue, field, field_label, field_price);
                            onBlur(field, localValue);
                        }}
                        required={required}
                    />
                </div>
            );
        
        case 'month':
            return (
                <div className="wc-block-components-month-input show_label_within_border">
                    <label>
                        {field_label}
                        {priceDisplay}
                    </label>
                    <input
                        id={field_name}
                        type="month"
                        className={isError ? 'has-error' : ''}
                        style={{ cursor: 'pointer', padding: '0.7em', width: '100%', borderColor: isError ? 'red' : 'initial' }}
                        onClick={(e) => e.target.showPicker && e.target.showPicker()}
                        value={localValue}
                        onChange={(e) => setLocalValue(e.target.value)}
                        onBlur={() => {
                            onChange(field_name, localValue, field, field_label, field_price);
                            onBlur(field, localValue);
                        }}
                        required={required}
                    />
                </div>
            );
        
        case 'datetime-local':
            return (
                <div className="wc-block-components-datetime-local-input show_label_within_border">
                    <label>
                        {field_label}
                        {priceDisplay}
                    </label>
                    <input
                        id={field_name}
                        type="datetime-local"
                        className={isError ? 'has-error' : ''}
                        style={{ cursor: 'pointer', padding: '0.7em', width: '100%', borderColor: isError ? 'red' : 'initial' }}
                        onClick={(e) => e.target.showPicker && e.target.showPicker()}
                        value={localValue}
                        onChange={(e) => setLocalValue(e.target.value)}
                        onBlur={() => {
                            onChange(field_name, localValue, field, field_label, field_price);
                            onBlur(field, localValue);
                        }}
                        required={required}
                    />
                </div>
            );

        case 'select':
            return (
                <div className="wc-blocks-components-select ccfw-select-block">
                    <div className="wc-blocks-components-select__container">
                        <label
                            htmlFor={field_name}
                            className=""
                        >
                            {field_label}
                        </label>

                        <Select
                            placeholder={t('select_option_placeholder', __('Select option..', 'conditional-checkout-fields-for-woocommerce'))}
                            inputId={field_name}
                            options={reactOptions}
                            value={reactOptions.find((opt) => opt.isDefault == opt.key && ('' != fieldValue && opt.key == fieldValue)) || reactOptions.find((opt) => fieldValue == opt.key) || null}
                            onChange={handleSelectChange}
                            onBlur={() => onBlur(field)}
                            className={isError ? "has-error" : ""}
                            classNamePrefix="ccfw-rs"
                            required={required}
                            isSearchable={true}
                        />

                    </div>

                    <style jsx>{`
                        .ccfw-rs__control {
                            border-color: ${isError ? '#cc1818 !important' : 'initial'};
                        }
                    `}</style>
                </div>
            );

        case 'multiselect':
            return (
                <div className="wc-blocks-components-select ccfw-select-block">
                    <div className="wc-blocks-components-select__container">
                        <label
                            htmlFor={field_name}
                            className=""
                        >
                            {field_label}
                        </label>

                        <Select
                            placeholder={field_label}
                            inputId={field_name}
                            options={reactOptions}
                            value={selectedValues}
                            onChange={handleSelectChange}
                            onBlur={() => onBlur(field)}
                            isMulti
                            className={isError ? "has-error" : ""}
                            classNamePrefix="ccfw-rs"
                            required={required}
                            isSearchable={true}
                        />
                    </div>
                </div>
            );

        case 'color':
            return (
                <div className="wc-block-components-color-input ccfw-color-block">
                    <label
                        htmlFor={field_name}
                        className=""
                    >
                        {field_label}
                        {priceDisplay}
                        {field.is_required == 1 && <abbr className="required" title="required">*</abbr>}
                    </label>
                    <TextInput
                        id={field_name}
                        type="color"
                        className={isError ? 'has-error' : ''}
                        value={localValue}
                        onChange={(value) => setLocalValue(value)}
                        onBlur={() => {
                            onChange(field_name, localValue, field, field_label, field_price);
                            onBlur(field, localValue);
                        }}
                        required={required}
                        style={{ width: '100%', height: '35px', marginTop: '0px !important' }}
                    />
                </div>
            );
        
        case 'url': {
            // Custom/confirm/other built-ins replace the URL format rule.
            const applyUrlRules = shouldApplyTypeFormatRule(field, 'url');
            return (
                <div className="wc-block-components-url-input">
                    <TextInput
                        label={field_label + priceDisplay}
                        id={field_name}
                        type="url"
                        className={isError ? 'has-error' : ''}
                        value={localValue}
                        onChange={(value) => setLocalValue(value)}
                        onBlur={() => {
                            onChange(field_name, localValue, field, field_label, field_price);
                            onBlur(field, localValue);
                        }}
                        required={required}
                    />
                    {validatorFormatHint ? (
                        <FormatHintNote hint={validatorFormatHint} />
                    ) : (
                        applyUrlRules && (
                            <span className="ccfw-url-input-note">{t('url_example', __('e.g. https://example.com', 'conditional-checkout-fields-for-woocommerce'))}</span>
                        )
                    )}
                    {applyUrlRules && localValue && !isValidURL(localValue) && (
                        <>
                            <br />
                            <span className="wc-block-components-validation-error" style={{ color: '#d63638' }}>
                                {t('url_validation_error', __('Please enter a valid URL', 'conditional-checkout-fields-for-woocommerce'))}
                            </span>
                        </>
                    )}
                </div>
            );
        }

        case 'label':
        case 'paragraph':
        case 'heading': {
            const headingContent = renderHeadingStylingField(field, isError);
            const TitleTag = headingContent.titleTag;
            const SubtitleTag = headingContent.subtitleTag;

            // We need to add proper spacing between both title and subtitle for all types.
            // Only apply extra bottom margin between title and subtitle for 'heading' fields, just like the label/paragraph.

            const isHeading = field.type === 'heading';
            const titleBottomMargin = headingContent.subtitle
                ? (isHeading ? '8px' : '4px') // headings need more space
                : '0px';

            return (
                <div
                    className={headingContent.wrapperClass}
                    style={{
                        marginTop: '8px',
                        marginBottom: '16px',
                        clear: 'both',
                    }}
                >
                    {headingContent.title ? (
                        TitleTag ? (
                            <TitleTag
                                {...headingContent.titleProps}
                                style={{
                                    ...(headingContent.titleProps?.style || {}),
                                    display: 'block',
                                    marginBottom: titleBottomMargin
                                }}
                            >
                                {headingContent.title}
                            </TitleTag>
                        ) : (
                            <span
                                {...headingContent.titleProps}
                                style={{
                                    ...(headingContent.titleProps?.style || {}),
                                    display: 'block',
                                    marginBottom: titleBottomMargin
                                }}
                            >
                                {headingContent.title}
                            </span>
                        )
                    ) : null}
                    {headingContent.subtitle ? (
                        <SubtitleTag
                            {...headingContent.subtitleProps}
                            style={{
                                ...(headingContent.subtitleProps?.style || {}),
                                display: 'block'
                            }}
                        >
                            {headingContent.subtitle}
                        </SubtitleTag>
                    ) : null}
                </div>
            );
        }

        default:
            if (hasInputMask(field)) {
                return (
                    <MaskedInputField
                        field={field}
                        field_name={field_name}
                        field_label={field_label}
                        fieldValue={fieldValue}
                        priceDisplay={priceDisplay}
                        localValue={localValue}
                        setLocalValue={setLocalValue}
                        isError={isError}
                        required={required}
                        onChange={onChange}
                        onBlur={onBlur}
                        field_price={field_price}
                        inputType="text"
                        fieldRef={fieldRef}
                        t={t}
                    />
                );
            }
            return (
                <>
                    <TextInput
                        label={field_label + priceDisplay}
                        id={field_name}
                        type="text"
                        className={`${isError ? 'has-error' : ''} my-custom-class`}
                        value={localValue}
                        onChange={(val) => setLocalValue(val)}
                        onBlur={() => {
                            onChange(field_name, localValue, field, field_label, field_price);
                            onBlur(field, localValue);
                        }}
                        required={required}
                        ref={fieldRef}
                    />
                    <FormatHintNote hint={validatorFormatHint} />
                </>
            );
    }

}

export default DynamicField;